import {
  Component,
  HostBinding,
  Input,
  computed,
  ChangeDetectionStrategy,
} from '@angular/core';
import { addDays, format } from 'date-fns';
import {
  NgpDatePickerCell,
  NgpDatePickerCellRender,
  NgpDatePickerDateButton,
  NgpDatePickerGrid,
  NgpDatePickerRowRender,
  injectDatePickerState,
  injectDateRangePickerState,
} from 'ng-primitives/date-picker';

import { SuiCalendarWeekNumberDirective } from './sui-calendar-week-number.directive';

interface Weekday {
  short: string;
  full: string;
  /** JS day-of-week index (0=Sun … 6=Sat) — stable @for track key. */
  key: number;
}

/**
 * The shared month grid — weekday header, day cells and an optional leading
 * ISO week-number column. Pure presentation around the ng-primitives grid
 * directives: it carries no value/CVA of its own. The `[ngpDatePicker]` /
 * `[ngpDateRangePicker]` root lives on the *parent* picker element, so the grid
 * directives here resolve the controller state through DI (the ng-primitives
 * grid directives find it for either single or range). Used by both
 * `sui-date-picker` and (later) `sui-date-range-picker`.
 */
@Component({
  selector: 'sui-calendar',
  standalone: true,
  imports: [
    NgpDatePickerGrid,
    NgpDatePickerRowRender,
    NgpDatePickerCellRender,
    NgpDatePickerCell,
    NgpDatePickerDateButton,
    SuiCalendarWeekNumberDirective,
  ],
  templateUrl: './sui-calendar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './sui-calendar.component.scss',
})
export class SuiCalendarComponent {
  /** Render the leading ISO week-number column (off by default; range turns it on). */
  @Input() showWeekNumbers = false;
  /** Compact density: 26px day squares instead of 32px. */
  @Input() @HostBinding('class.sui-calendar--compact') compact = false;

  /**
   * Accessible name for the grid (the month/year). Set directly rather than via
   * the primitive's `ngpDatePickerLabel` contentChild, because in the field/popover
   * presentation the calendar is portaled out of the `[ngpDatePicker]` subtree, so
   * that content query can't reach the label — `aria-label` works in both modes.
   */
  @Input() ariaLabel?: string;

  // The controller state lives on the ancestor [ngpDatePicker] /
  // [ngpDateRangePicker] element. The primitive's own `injectDateControllerState`
  // (which picks whichever is present) is not part of the public API, so we
  // resolve both optionally and use whichever resolved — single or range.
  private readonly _single = injectDatePickerState({ optional: true });
  private readonly _range = injectDateRangePickerState({ optional: true });

  /**
   * The weekday header labels, ordered by the controller's `firstDayOfWeek`
   * (primitive numbering: 1=Mon … 7=Sun, 0/7 = Sunday). Generated with date-fns
   * off a known Sunday so the labels follow the locale and the configured start.
   */
  readonly weekdays = computed<Weekday[]>(() => {
    const ctrl = this._single() ?? this._range();
    const startDow = (ctrl?.firstDayOfWeek() ?? 7) % 7; // 7→0 (Sun), 1→1 (Mon) … 6→6 (Sat)
    const sunday = new Date(2024, 0, 7); // 2024-01-07 is a Sunday
    return Array.from({ length: 7 }, (_, i) => {
      const dow = (startDow + i) % 7;
      const day = addDays(sunday, dow);
      return {
        short: format(day, 'EEEEEE'), // two-letter ("Mo")
        full: format(day, 'EEEE'), // full ("Monday") for the th abbr
        key: dow,
      };
    });
  });
}
